import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Globe, Loader2, Check, X } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import broadcastAdminService, { ReferenceType } from '../../services/broadcastAdminService'

type TabId = ReferenceType

const TABS: { id: TabId; name: string }[] = [
  { id: 'sports', name: 'Sports' },
  { id: 'leagues', name: 'Leagues' },
  { id: 'teams', name: 'Teams' },
  { id: 'venues', name: 'Venues' },
  { id: 'viewership-sources', name: 'Viewership sources' },
  { id: 'competition-stages', name: 'Competition stages' }
]

/**
 * Platform reference data — sports, leagues, teams, venues, viewership sources.
 *
 * NOT reachable from an organization, and no customerId anywhere on this page.
 * Brisbane Bandits is the same team whoever is reporting on them, and a form
 * headed with a customer's name must not contain a control that disables a
 * league for everyone.
 *
 * Nothing is ever deleted — is_active only. A deleted team with broadcasts
 * pointing at it is a broken report.
 */
const ReferenceDataPage: React.FC = () => {
  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()
  const [activeTab, setActiveTab] = useState<TabId>('teams')
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showInactive, setShowInactive] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await broadcastAdminService.listReference<any>(activeTab, showInactive)
      setRows(res.data || [])
    } catch (err) {
      showError('Failed to load', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setLoading(false)
    }
  }, [activeTab, showInactive, showError])

  useEffect(() => { void load() }, [load])

  const toggleActive = async (row: any) => {
    try {
      await broadcastAdminService.updateReference(activeTab, row.id, { isActive: !row.is_active })
      showSuccess(row.is_active ? 'Disabled' : 'Enabled', row.name)
      await load()
    } catch (err) {
      showError('Could not update', err instanceof Error ? err.message : 'Please try again')
    }
  }

  const columns: Record<TabId, { key: string; label: string }[]> = {
    sports: [{ key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }],
    leagues: [
      { key: 'short_name', label: 'Short' }, { key: 'name', label: 'Name' },
      { key: 'kind', label: 'Kind' }, { key: 'sport_code', label: 'Sport' },
      { key: 'season_format', label: 'Season' }, { key: 'country', label: 'Country' }
    ],
    teams: [
      { key: 'abbreviation', label: 'Abbr' }, { key: 'name', label: 'Name' },
      { key: 'league_short_name', label: 'League' }, { key: 'sport_code', label: 'Sport' },
      { key: 'home_venue_name', label: 'Home venue' }
    ],
    venues: [
      { key: 'name', label: 'Name' }, { key: 'city', label: 'City' },
      { key: 'region', label: 'Region' }, { key: 'country', label: 'Country' },
      { key: 'timezone', label: 'Timezone' }
    ],
    'viewership-sources': [
      { key: 'name', label: 'Name' }, { key: 'code', label: 'Code' },
      { key: 'is_estimate', label: 'Quality' }
    ],
    'competition-stages': [
      { key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }
    ]
  }

  const render = (row: any, key: string) => {
    if (key === 'is_estimate') {
      return (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${row.is_estimate ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
          {row.is_estimate ? 'Estimate' : 'Measured'}
        </span>
      )
    }
    return row[key] ?? <span className="text-gray-300">—</span>
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate('/support')}
          className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-800 mb-3 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Support
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Globe className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reference Data</h1>
            <p className="text-xs text-gray-500">Platform-wide. Changes here affect every customer.</p>
          </div>
        </div>

        <div className="bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 mb-5">
          <p className="text-xs text-violet-900">
            These lookups are shared across the platform — the same team and venue appear for
            every customer. Nothing is deleted here; disable instead, because a removed team
            with broadcasts pointing at it is a broken report.
          </p>
        </div>

        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-md text-xs font-semibold transition-all whitespace-nowrap ${
                  activeTab === t.id
                    ? 'bg-white text-violet-700 shadow-md border border-white/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                   className="rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
            Show disabled
          </label>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left">
                  {columns[activeTab].map((c) => (
                    <th key={c.key} className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{c.label}</th>
                  ))}
                  <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 && (
                  <tr><td colSpan={columns[activeTab].length + 1} className="px-4 py-8 text-center text-gray-400 text-xs">Nothing here yet.</td></tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id} className={`hover:bg-gray-50 ${row.is_active ? '' : 'opacity-50'}`}>
                    {columns[activeTab].map((c) => (
                      <td key={c.key} className="px-4 py-2 text-gray-700">{render(row, c.key)}</td>
                    ))}
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => toggleActive(row)}
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                          row.is_active
                            ? 'text-emerald-700 hover:bg-emerald-50'
                            : 'text-gray-500 hover:bg-gray-100'
                        }`}
                        title={row.is_active ? 'Disable' : 'Enable'}
                      >
                        {row.is_active ? <><Check className="h-3.5 w-3.5 mr-1" />Active</> : <><X className="h-3.5 w-3.5 mr-1" />Disabled</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReferenceDataPage
